import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu } from 'lucide-react';
import logo from '../../assets/logo/logo_banco_alimentare.svg';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../ui/sheet';
import {
  isNavItemActive,
  primaryNavItems,
  settingsNavItem,
} from '../../lib/navItems';

function NavLinkItem({ to, label, Icon, exact, isActive, onNavigate, mobile = false }) {
  return (
    <Link
      to={to}
      title={label}
      onClick={onNavigate}
      className={cn(
        'flex items-center gap-2 font-semibold transition-colors',
        mobile
          ? 'gap-3 rounded-lg px-3 py-3 text-base'
          : 'rounded-lg px-3 py-2 text-sm',
        isActive
          ? mobile
            ? 'bg-brand-orange/10 text-brand-orange'
            : 'text-brand-orange'
          : mobile
            ? 'text-[var(--ba-navbar-link-color)] hover:bg-black/[0.04] hover:text-brand-orange'
            : 'text-[var(--ba-navbar-link-color)] hover:text-brand-orange'
      )}
    >
      <Icon className={mobile ? 'h-5 w-5 shrink-0' : 'h-4 w-4'} />
      {label}
    </Link>
  );
}

function Navbar() {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const allNavItems = [...primaryNavItems, settingsNavItem];

  return (
    <>
      <nav className="ba-app-nav sticky top-0 z-40 hidden border-b shadow-sm lg:block">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <Link to="/" className="shrink-0">
            <img src={logo} alt="Banco Alimentare" className="h-12 w-auto" />
          </Link>

          <ul className="m-0 flex list-none items-center gap-1 p-0">
            {allNavItems.map((item) => (
              <li key={item.to}>
                <NavLinkItem
                  {...item}
                  isActive={isNavItemActive(location.pathname, item)}
                />
              </li>
            ))}
          </ul>
        </div>
      </nav>

      <nav
        className="ba-app-nav fixed inset-x-0 top-0 z-40 border-b shadow-sm lg:hidden"
        aria-label="Navigazione principale"
      >
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <Link to="/" className="shrink-0">
            <img src={logo} alt="Banco Alimentare" className="h-10 w-auto" />
          </Link>

          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="icon"
                aria-label="Apri menu di navigazione"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[min(100vw,18rem)] max-w-[18rem] p-0">
              <SheetHeader className="border-b border-[var(--ba-surface-border)] px-5 py-4 pr-12">
                <SheetTitle className="sr-only">Menu di navigazione</SheetTitle>
                <Link to="/" onClick={() => setMenuOpen(false)} className="inline-block">
                  <img src={logo} alt="Banco Alimentare" className="h-10 w-auto" />
                </Link>
              </SheetHeader>

              <div className="flex flex-1 flex-col px-3 py-4">
                <ul className="m-0 flex list-none flex-col gap-1 p-0">
                  {primaryNavItems.map((item) => (
                    <li key={item.to}>
                      <NavLinkItem
                        {...item}
                        mobile
                        isActive={isNavItemActive(location.pathname, item)}
                        onNavigate={() => setMenuOpen(false)}
                      />
                    </li>
                  ))}
                </ul>

                <div className="mt-auto border-t border-[var(--ba-surface-border)] pt-4">
                  <NavLinkItem
                    {...settingsNavItem}
                    mobile
                    isActive={isNavItemActive(location.pathname, settingsNavItem)}
                    onNavigate={() => setMenuOpen(false)}
                  />
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
      <div className="ba-mobile-nav-spacer lg:hidden" aria-hidden="true" />
    </>
  );
}

export default Navbar;
